/* eslint-disable jsx-a11y/anchor-is-valid */
import _ from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { DismissRegular } from '@fluentui/react-icons';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Button, Link, Toaster, Toast, ToastTitle, ToastBody, ToastFooter, useId, useToastController } from '@fluentui/react-components';
import { useWizardService, getCurrentStepId, getWizardData, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS } from '../step-utils';

const getStyles = () => ({
  toaster: {
    '&.fui-ToastTitle': {
      alignItems: 'center',
    },
  },
});

export const BackSummaryToast = () => {
  const [rootEle, setRootEle] = useState(null);
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);
  const { state, send } = useWizardService();
  const classes = useDefaultStyles(getStyles);
  const currentStepId = getCurrentStepId(state);
  const toasterId = useId('back-to-summary-toaster');
  const { dispatchToast, dismissToast } = useToastController(toasterId);

  const handleSaveAndReturn = useCallback(() => {
    if (activity) {
      activity.signal(`onSaveAndBackToSummary from ${currentStepId}`);
    }
    send({
      type: WIZARD_EVENTS.SAVE_AND_GOTO,
      stepId: SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS.SUMMARY,
      wizardData: {
        focusedStepId: '',
      },
    });
  }, [send, activity]);

  const handleDismiss = useCallback(() => {
    if (activity) {
      activity.signal('onDismissBackToSummaryToast');
    }
    send({
      type: WIZARD_EVENTS.UPDATE_WIZARD_DATA,
      focusedStepId: '',
    });
    dismissToast(toasterId);
  }, [send, dismissToast, toasterId, activity]);

  const backToSummaryToast = useMemo(() => {
    const toast = (
      <Toast>
        <ToastTitle media={null} action={<Button size="small" aria-label={i18n.getString(_TL_('Dismiss'))} appearance="transparent" icon={<DismissRegular />} onClick={handleDismiss} />}>{i18n.getString(_TL_('Edit mode'))}</ToastTitle>
        <ToastBody>{i18n.getString(_TL_('Please review your changes before returning to the summary page.'))}</ToastBody>
        <ToastFooter>
          <Link onClick={handleDismiss}>{i18n.getString(_TL_('Dismiss'))}</Link>
          <Link onClick={handleSaveAndReturn}>{i18n.getString(_TL_('Save & return to Summary'))}</Link>
        </ToastFooter>
      </Toast>);
    return toast;
  }, [handleDismiss, handleSaveAndReturn, i18n]);

  useEffect(() => {
    const wizardData = getWizardData(state);
    if (_.get(wizardData, ['focusedStepId']) === currentStepId) {
      dispatchToast(backToSummaryToast, { toastId: toasterId, timeout: -1 });
    }
    if (currentStepId === SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS.SUMMARY) {
      dismissToast(toasterId);
    }
  }, [currentStepId, state, dispatchToast, dismissToast, toasterId, backToSummaryToast]);

  return (
    <>
      <div ref={setRootEle} style={{ position: 'relative' }} />
      <Toaster className={`back-to-summary-toaster ${classes.toaster}`} toasterId={toasterId} mountNode={rootEle} position="bottom-end" offset={{ vertical: 150 }} />
    </>
  );
};
